// Admin-only: send a mock payment confirmation email.
// Lets us verify branding/domain/triggers before going live.

import { createServerFn } from '@tanstack/react-start'
import { requireSupabaseAuth } from '@/integrations/supabase/auth-middleware'
import { z } from 'zod'

const inputSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(120).optional(),
  planKey: z.enum(['basic_support', 'full_support', 'refusal_support']).optional(),
})

export const sendTestPaymentEmail = createServerFn({ method: 'POST' })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) => inputSchema.parse(data))
  .handler(async ({ data, context }) => {
    // Caller must be admin
    const { data: isAdmin } = await context.supabase.rpc('has_role', {
      _user_id: context.userId,
      _role: 'admin',
    })
    if (!isAdmin) throw new Error('Forbidden')

    const recipient = data.email.trim().toLowerCase()
    const { assertAllowedTestRecipient } = await import('@/lib/email/test-recipient.server')
    await assertAllowedTestRecipient(recipient, context.claims?.email as string | undefined)
    const planKey = data.planKey ?? 'full_support'
    const planName =
      planKey === 'basic_support'
        ? 'Basic Support'
        : planKey === 'refusal_support'
          ? 'Refusal Support'
          : 'Full Support'

    const templateData = {
      name: data.name || 'there',
      planKey,
      planName,
      amountInr: planKey === 'basic_support' ? 3000 : planKey === 'refusal_support' ? 7000 : 4500,
      dashboardUrl: 'https://welivdit.com/student-dashboard',
    }

    const idempotencyKey = `test-payment-confirmation-${crypto.randomUUID()}`

    const { sendTemplateEmail } = await import('@/lib/email-templates/send-email')
    const { supabaseAdmin } = await import('@/integrations/supabase/client.server')

    const logSend = async (status: 'sent' | 'suppressed' | 'failed', errorMessage?: string) => {
      const { error: logError } = await supabaseAdmin.from('email_send_log').insert({
        template_name: 'payment-confirmation',
        recipient_email: recipient,
        status,
        error_message: errorMessage ?? null,
      })
      if (logError) {
        console.error('Failed to write email_send_log row', {
          code: logError.code,
          message: logError.message,
        })
      }
    }

    try {
      const result = await sendTemplateEmail('payment-confirmation', recipient, {
        templateData,
        idempotencyKey,
      })
      if (!result.sent) {
        await logSend('suppressed')
        return { sent: false, reason: 'recipient_suppressed', recipient, wasSuppressed: true }
      }
      await logSend('sent')
      return { sent: true, recipient, messageId: idempotencyKey, wasSuppressed: false }
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      console.error('sendTestPaymentEmail: send failed', message)
      await logSend('failed', message.slice(0, 1000))
      return { sent: false, reason: message, recipient }
    }
  })
